// src/pages/Communication.jsx
// CRUD réel via Worker Admin + visualisation avancée (démo) pour Worker Notification
import React, { useState, useEffect, useMemo } from 'react';
import {
  MessageSquare,
  Plus,
  Filter,
  Archive,
  Edit3,
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  LayoutDashboard,
  History,
  BarChart3,
  Send,
  Zap,
  Radio,
  Activity,
  Users,
  Globe,
  Bell,
  X,
} from 'lucide-react';
import {
  PageHeader,
  StatusBadge,
  DonutChart,
  HorizontalBars,
  AreaChart,
  EmptyState,
  DemoDataBanner,
} from '../components/ui/UIComponents';
import NotificationModal from '../components/ui/NotificationModal';
import * as adminApi from '../services/adminNotifications';
import { useI18n } from '../i18n/useI18n';

const TABS = [
  { id: 'overview', label: "Vue d'ensemble", icon: LayoutDashboard },
  { id: 'notifications', label: 'Notifications', icon: MessageSquare },
  { id: 'create', label: 'Nouvelle notification', icon: Plus },
  { id: 'diffusions', label: 'Diffusions', icon: Send },
  { id: 'auto', label: 'Automatiques', icon: Zap },
  { id: 'history', label: 'Historique', icon: History },
  { id: 'stats', label: 'Statistiques', icon: BarChart3 },
  { id: 'control', label: 'Contrôle', icon: Radio },
];

const CHART_COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#f43f5e', '#0ea5e9', '#8b5cf6', '#64748b', '#ec4899'];

// ——— DÉMONSTRATION UNIQUEMENT (Worker Notification non existant) ———
const DEMO_TEMPORAL = [12, 25, 18, 42, 35, 51, 29];
const DEMO_TEMPORAL_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const DEMO_CATEGORIES = [
  { label: 'Paiement', value: 42 },
  { label: 'Contenu', value: 31 },
  { label: 'Communauté', value: 24 },
  { label: 'Marketplace', value: 18 },
  { label: 'Boutique', value: 14 },
  { label: 'Sécurité', value: 9 },
  { label: 'Administration', value: 7 },
  { label: 'Système', value: 5 },
  { label: 'Information', value: 22 },
];

const DEMO_ORIGINS = [
  { label: 'Worker Paiement', value: 38, color: '#10b981' },
  { label: 'Worker Contenu', value: 28, color: '#4f46e5' },
  { label: 'Worker Admin', value: 22, color: '#0ea5e9' },
  { label: 'Worker Boutique', value: 16, color: '#f59e0b' },
  { label: 'Worker Marketplace', value: 12, color: '#f43f5e' },
  { label: 'Worker Communauté', value: 10, color: '#8b5cf6' },
];

const DEMO_CHANNELS = [
  { name: 'In-App', state: 'actif_demo', note: 'Démo' },
  { name: 'Push', state: 'preparation', note: 'Préparation' },
  { name: 'Email', state: 'preparation', note: 'Préparation' },
  { name: 'WhatsApp', state: 'futur', note: 'Futur' },
  { name: 'Telegram', state: 'futur', note: 'Futur' },
];

const DEMO_DELIVERY = [
  { label: 'Envoyées', value: 820, color: '#4f46e5' },
  { label: 'Délivrées', value: 794, color: '#10b981' },
  { label: 'En attente', value: 12, color: '#f59e0b' },
  { label: 'Échouées', value: 14, color: '#f43f5e' },
];

const DEMO_DIFFUSION = [
  { label: 'Individuelles', value: 210, color: '#4f46e5' },
  { label: 'Collectives', value: 95, color: '#10b981' },
  { label: 'Globales', value: 18, color: '#f59e0b' },
];

const DEMO_TIMELINE = [
  { time: '10:42', text: 'Notification paiement envoyée', tag: 'Paiement' },
  { time: '10:38', text: 'Notification contenu créée', tag: 'Contenu' },
  { time: '10:31', text: 'Notification collective activée', tag: 'Admin' },
  { time: '10:25', text: 'Événement Marketplace reçu', tag: 'Marketplace' },
  { time: '10:18', text: 'Notification administrative créée', tag: 'Admin' },
];

const DEMO_WORKER_STATUS = [
  { name: 'Worker Paiement', state: 'connected', label: 'Connecté (démo)' },
  { name: 'Worker Contenu', state: 'events', label: 'Événements disponibles (démo)' },
  { name: 'Worker Boutique', state: 'prep', label: 'Préparation' },
  { name: 'Worker Marketplace', state: 'prep', label: 'Préparation' },
  { name: 'Worker Communauté', state: 'prep', label: 'Préparation' },
  { name: 'Worker Notification', state: 'future', label: 'Futur — non développé' },
];

const DEMO_CONTROL = [
  { cat: 'Paiement', state: 'Activé' },
  { cat: 'Contenu', state: 'Activé' },
  { cat: 'Boutique', state: 'Activé' },
  { cat: 'Marketplace', state: 'Activé' },
  { cat: 'Communauté', state: 'Activé' },
  { cat: 'Sécurité', state: 'Activé' },
  { cat: 'Email', state: 'Préparation' },
  { cat: 'WhatsApp', state: 'Futur' },
  { cat: 'Telegram', state: 'Futur' },
  { cat: 'Push', state: 'Futur' },
];

export default function Communication() {
  const { t } = useI18n();
  const [tab, setTab] = useState('overview');
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [statutFilter, setStatutFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNotif, setEditingNotif] = useState(null);
  const [detailNotif, setDetailNotif] = useState(null);

  const fetchNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.getNotifications({
        statut: statutFilter || undefined,
        type: typeFilter || undefined,
      });
      setNotifications(response.notifications || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [statutFilter, typeFilter]);

  const handleCreateOrUpdate = async (payload) => {
    if (editingNotif) {
      await adminApi.updateNotification(payload);
      setSuccessMsg('Notification mise à jour avec succès.');
    } else {
      await adminApi.createNotification(payload);
      setSuccessMsg('Notification créée en mode brouillon.');
    }
    fetchNotifications();
  };

  const handleArchive = async (notif) => {
    if (!window.confirm(`Voulez-vous vraiment archiver la notification "${notif.titre}" ?`)) return;
    try {
      await adminApi.archiveNotification(notif.id);
      setSuccessMsg('Notification archivée.');
      fetchNotifications();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleStatus = async (notif, newStatus) => {
    try {
      await adminApi.updateNotification({ id: notif.id, statut: newStatus });
      setSuccessMsg(`Statut passé à "${newStatus}".`);
      fetchNotifications();
    } catch (err) {
      setError(err.message);
    }
  };

  const kpisReal = useMemo(() => {
    const total = notifications.length;
    const active = notifications.filter((n) => n.statut === 'active').length;
    const brouillon = notifications.filter((n) => n.statut === 'brouillon').length;
    const archivee = notifications.filter((n) => n.statut === 'archivee').length;
    return { total, active, brouillon, archivee };
  }, [notifications]);

  const statusSegmentsReal = useMemo(() => {
    const map = {};
    notifications.forEach((n) => {
      const s = n.statut || 'inconnu';
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).map(([label, value], i) => ({
      label,
      value,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));
  }, [notifications]);

  const typeBarsReal = useMemo(() => {
    const map = {};
    notifications.forEach((n) => {
      const t = n.type || 'autre';
      map[t] = (map[t] || 0) + 1;
    });
    return Object.entries(map).map(([label, value]) => ({
      label,
      value,
      color: 'bg-indigo-500',
    }));
  }, [notifications]);

  const categoryBarsDemo = useMemo(
    () => DEMO_CATEGORIES.map((c) => ({ label: c.label, value: c.value, color: 'bg-indigo-500' })),
    []
  );

  const originBarsDemo = useMemo(
    () => DEMO_ORIGINS.map((o) => ({ label: o.label, value: o.value, color: 'bg-violet-500' })),
    []
  );

  const openCreate = () => {
    setEditingNotif(null);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title={t("pages.communication.title")}
        subtitle={t("pages.communication.subtitle")}
        workerName="Worker Admin"
        actions={
          <div className="flex flex-wrap gap-2">
            <button onClick={fetchNotifications} className="dsi-btn-secondary text-xs">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
            </button>
            <button onClick={openCreate} className="dsi-btn-primary text-xs">
              <Plus size={14} /> Nouvelle notification
            </button>
          </div>
        }
      />

      {successMsg && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-700 text-xs font-bold flex items-center justify-between">
          <div className="flex items-center gap-2"><CheckCircle2 size={16} /><span>{successMsg}</span></div>
          <button onClick={() => setSuccessMsg(null)} className="text-emerald-700 hover:underline">{t('common.close')}</button>
        </div>
      )}
      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-rose-700 text-xs font-bold flex items-center justify-between">
          <div className="flex items-center gap-2"><AlertCircle size={16} /><span>{error}</span></div>
          <button onClick={fetchNotifications} className="dsi-btn-secondary text-xs">{t('common.retry')}</button>
        </div>
      )}

      <div className="flex flex-wrap gap-1 bg-white border border-slate-200 p-1.5 rounded-2xl">
        {TABS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all ${
                tab === item.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <Icon size={14} /> {item.labelKey ? t(item.labelKey) : item.label}
            </button>
          );
        })}
      </div>

      {/* ========== OVERVIEW ========== */}
      {tab === 'overview' && (
        <div className="space-y-4">
          {/* KPI réels + démo */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            {[
              { label: 'Total (réel)', value: loading ? '…' : kpisReal.total, real: true },
              { label: 'Actives (réel)', value: loading ? '…' : kpisReal.active, real: true },
              { label: 'Brouillons (réel)', value: loading ? '…' : kpisReal.brouillon, real: true },
              { label: 'Envoyées', value: '820', real: false },
              { label: 'Individuelles', value: '210', real: false },
              { label: 'Collectives', value: '95', real: false },
            ].map((k) => (
              <div key={k.label} className="dsi-card p-4">
                <p className="text-[10px] font-semibold uppercase text-slate-500">{k.label}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{k.value}</p>
                <p className={`text-[10px] font-medium mt-1 ${k.real ? 'text-slate-400' : 'text-amber-700'}`}>
                  {k.real ? 'Worker Admin' : 'Données de démonstration'}
                </p>
              </div>
            ))}
          </div>

          {/* Graphique temporel démo */}
          <div className="dsi-card p-5">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Évolution des notifications</h3>
                <p className="text-xs text-slate-500">Volume hebdomadaire — démonstration graphique</p>
              </div>
              <DemoDataBanner workerName="Worker Notification" />
            </div>
            <AreaChart series={DEMO_TEMPORAL} />
            <div className="mt-2 flex justify-between text-[10px] text-slate-400">
              {DEMO_TEMPORAL_LABELS.map((d) => (
                <span key={d}>{d}</span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="dsi-card p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900">Par catégorie</h3>
                <span className="text-[10px] text-amber-700 font-semibold">DÉMO</span>
              </div>
              <HorizontalBars rows={categoryBarsDemo} />
            </div>
            <div className="dsi-card p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900">Origine des communications</h3>
                <span className="text-[10px] text-amber-700 font-semibold">DÉMO</span>
              </div>
              <HorizontalBars rows={originBarsDemo} />
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Statuts (données réelles)</h3>
              <p className="text-xs text-slate-500 mb-3">Liste Worker Admin</p>
              {statusSegmentsReal.length ? (
                <DonutChart segments={statusSegmentsReal} centerValue={kpisReal.total} centerLabel="Total" />
              ) : (
                <EmptyState title="Aucune notification" message="Pas encore de données réelles." />
              )}
            </div>
            <div className="dsi-card p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-slate-900">Délivrabilité</h3>
                <span className="text-[10px] text-amber-700 font-semibold">DÉMO</span>
              </div>
              <DonutChart segments={DEMO_DELIVERY} centerValue="98%" centerLabel="Santé" />
            </div>
            <div className="dsi-card p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-slate-900">Type de diffusion</h3>
                <span className="text-[10px] text-amber-700 font-semibold">DÉMO</span>
              </div>
              <DonutChart segments={DEMO_DIFFUSION} centerValue={DEMO_DIFFUSION.reduce((s, x) => s + x.value, 0)} centerLabel="Total" />
            </div>
          </div>

          {/* Santé + canaux */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="dsi-card p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900">Santé du système</h3>
                <DemoDataBanner workerName="Worker Notification" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Notifications', '98,4 %'],
                  ['Délivrabilité', '96,8 %'],
                  ['Échecs', '1,6 %'],
                  ['En attente', '0,8 %'],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[10px] uppercase text-slate-400 font-semibold">{k}</p>
                    <p className="text-xl font-bold text-slate-900 mt-1">{v}</p>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-amber-700 mt-3 font-medium">Données de démonstration</p>
            </div>
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Canaux de diffusion</h3>
              <div className="space-y-2">
                {DEMO_CHANNELS.map((c) => (
                  <div key={c.name} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="text-sm font-semibold text-slate-800">{c.name}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${
                      c.state === 'actif_demo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      c.state === 'preparation' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-slate-100 text-slate-500 border-slate-200'
                    }`}>{c.note}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Timeline + workers */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="dsi-card p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Activity size={16} /> Timeline</h3>
                <span className="text-[10px] text-amber-700 font-semibold">DÉMO</span>
              </div>
              <div className="relative border-l-2 border-slate-200 ml-2 space-y-3">
                {DEMO_TIMELINE.map((e, i) => (
                  <div key={i} className="relative pl-5">
                    <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-white border-2 border-indigo-400" />
                    <p className="text-[11px] text-slate-400 font-mono">{e.time}</p>
                    <p className="text-sm text-slate-800 font-medium">{e.text}</p>
                    <span className="text-[10px] text-indigo-600 font-semibold">{e.tag}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">États des Workers</h3>
              <div className="space-y-2">
                {DEMO_WORKER_STATUS.map((w) => (
                  <div key={w.name} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
                    <span className="text-sm font-medium text-slate-800">{w.name}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                      w.state === 'connected' || w.state === 'events' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      w.state === 'prep' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-blue-50 text-blue-700 border-blue-200'
                    }`}>{w.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== NOTIFICATIONS RÉELLES ========== */}
      {tab === 'notifications' && (
        <div className="space-y-4">
          <div className="dsi-card p-4 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <Filter size={14} className="text-slate-400" />
              <select value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)} className="dsi-input w-auto">
                <option value="">Tous les statuts</option>
                <option value="brouillon">Brouillon</option>
                <option value="active">Active</option>
                <option value="archivee">Archivée</option>
              </select>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="dsi-input w-auto">
                <option value="">Tous les types</option>
                <option value="information">Information</option>
                <option value="maintenance">Maintenance</option>
                <option value="nouveaute">Nouveauté</option>
                <option value="alerte">Alerte</option>
              </select>
            </div>
            <button onClick={openCreate} className="dsi-btn-primary text-xs"><Plus size={16} /> Nouvelle notification</button>
          </div>

          <div className="dsi-card overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <RefreshCw size={24} className="animate-spin text-indigo-500 mx-auto" />
                <p className="text-xs font-bold text-slate-400 mt-2">Chargement des notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <EmptyState title="Aucune notification trouvée" message="Ajustez vos filtres ou créez une nouvelle notification." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs text-slate-600">
                  <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="p-4">Notification</th>
                      <th className="p-4">Type</th>
                      <th className="p-4">Statut</th>
                      <th className="p-4">Période d'affichage</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {notifications.map((n) => (
                      <tr key={n.id} className="hover:bg-slate-50 transition-colors">
                        <td className="p-4">
                          <button onClick={() => setDetailNotif(n)} className="text-left">
                            <p className="font-bold text-slate-900 text-sm hover:text-indigo-700">{n.titre}</p>
                            <p className="text-slate-500 text-[11px] line-clamp-1 mt-0.5">{n.message}</p>
                            <p className="text-[10px] text-slate-400 mt-1 font-mono">
                              Créé le {n.created_at ? new Date(n.created_at).toLocaleDateString('fr-FR') : '—'}
                              {n.cree_par_id && ` • Par Admin #${n.cree_par_id}`}
                            </p>
                          </button>
                        </td>
                        <td className="p-4">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border ${
                            n.type === 'alerte' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                            n.type === 'maintenance' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                            n.type === 'nouveaute' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                            'bg-slate-50 text-slate-600 border-slate-200'
                          }`}>{n.type}</span>
                        </td>
                        <td className="p-4">
                          <StatusBadge
                            statusKey={n.statut === 'active' ? 'ACTIF' : n.statut === 'archivee' ? 'INACTIF' : 'EN_ATTENTE'}
                            customLabel={n.statut}
                          />
                        </td>
                        <td className="p-4 font-mono text-[11px] text-slate-500">
                          {n.date_debut_affichage
                            ? new Date(n.date_debut_affichage).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                            : 'Début imméd.'}
                          <br />➔{' '}
                          {n.date_fin_affichage
                            ? new Date(n.date_fin_affichage).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
                            : 'Indéterminée'}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {n.statut === 'brouillon' && (
                              <button onClick={() => handleToggleStatus(n, 'active')} className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl text-[10px] font-bold hover:bg-emerald-100">Publier</button>
                            )}
                            <button onClick={() => { setEditingNotif(n); setIsModalOpen(true); }} className="p-1.5 text-slate-500 hover:text-slate-900 bg-white rounded-xl border border-slate-200" title="Modifier"><Edit3 size={14} /></button>
                            {n.statut !== 'archivee' && (
                              <button onClick={() => handleArchive(n)} className="p-1.5 text-rose-700 bg-rose-50 rounded-xl border border-rose-200" title="Archiver"><Archive size={14} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CREATE */}
      {tab === 'create' && (
        <div className="dsi-card p-6 space-y-4 max-w-2xl">
          <h3 className="text-sm font-semibold text-slate-900">Nouvelle notification</h3>
          <p className="text-xs text-slate-500">Formulaire actuel via Worker Admin. Ciblage multi-canal = préparation Worker Notification.</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { icon: Users, label: 'Individuelle', note: 'Futur' },
              { icon: Users, label: 'Collective', note: 'Futur' },
              { icon: Globe, label: 'Globale', note: 'Futur' },
            ].map((card) => {
              const CIcon = card.icon;
              return (
              <div key={card.label} className="rounded-xl border border-dashed border-slate-200 p-3 text-center">
                <CIcon size={16} className="mx-auto text-slate-400" />
                <p className="text-xs font-semibold text-slate-700 mt-1">{card.label}</p>
                <p className="text-[10px] text-amber-700">{card.note}</p>
              </div>
            );})}
          </div>
          <button onClick={openCreate} className="dsi-btn-primary text-xs"><Plus size={14} /> Ouvrir le formulaire (contrat actuel)</button>
          <DemoDataBanner workerName="Worker Notification" />
        </div>
      )}

      {/* DIFFUSIONS */}
      {tab === 'diffusions' && (
        <div className="space-y-4">
          <DemoDataBanner workerName="Worker Notification" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {DEMO_DIFFUSION.map((d) => (
              <div key={d.label} className="dsi-card p-4">
                <p className="text-[10px] uppercase text-slate-500 font-semibold">{d.label}</p>
                <p className="text-2xl font-bold mt-1">{d.value}</p>
                <p className="text-[10px] text-amber-700 mt-1">DÉMO</p>
              </div>
            ))}
          </div>
          <div className="dsi-card p-5">
            <h3 className="text-sm font-semibold mb-3">Canaux</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {DEMO_CHANNELS.map((c) => (
                <div key={c.name} className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center">
                  <Bell size={16} className="mx-auto text-indigo-500" />
                  <p className="text-xs font-semibold mt-1">{c.name}</p>
                  <p className="text-[10px] text-amber-700">{c.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AUTO */}
      {tab === 'auto' && (
        <div className="dsi-card p-6 space-y-4">
          <DemoDataBanner workerName="Worker Notification" />
          <h3 className="text-sm font-semibold text-slate-900">Événements générateurs (architecture future)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 text-xs">
            {[
              ['Paiement', 'Confirmé · Échoué · Échéance'],
              ['Contenu', 'Nouveau · Disponible · Événement'],
              ['Marketplace', 'Commande · Vendeur · Listing'],
              ['Boutique', 'Commande · Livraison · Promo'],
              ['Communauté', 'Message · Invitation · Événement'],
              ['Administration', 'Alerte · Maintenance · Info'],
            ].map(([title, items]) => (
              <div key={title} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <p className="font-semibold text-slate-900">{title}</p>
                <p className="text-slate-500 mt-1">{items}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-amber-700 font-medium">Préparation — aucun événement inventé comme opérationnel</p>
        </div>
      )}

      {/* HISTORY */}
      {tab === 'history' && (
        <div className="dsi-card overflow-hidden">
          {loading ? (
            <div className="p-12 text-center"><RefreshCw size={24} className="animate-spin text-indigo-500 mx-auto" /></div>
          ) : notifications.length === 0 ? (
            <EmptyState title="Aucun historique" message="Aucune notification disponible." />
          ) : (
            <div className="divide-y divide-slate-100">
              {notifications.map((n) => (
                <button key={n.id} onClick={() => setDetailNotif(n)} className="w-full p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-left hover:bg-slate-50">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{n.titre}</p>
                    <p className="text-[11px] text-slate-500">{n.type} · {n.statut} · {n.created_at ? new Date(n.created_at).toLocaleString('fr-FR') : '—'}</p>
                  </div>
                  <StatusBadge statusKey={n.statut === 'active' ? 'ACTIF' : n.statut === 'archivee' ? 'INACTIF' : 'EN_ATTENTE'} customLabel={n.statut} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STATS */}
      {tab === 'stats' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold mb-3">Statuts réels (Worker Admin)</h3>
              {statusSegmentsReal.length ? (
                <DonutChart segments={statusSegmentsReal} centerValue={kpisReal.total} centerLabel="Total" />
              ) : (
                <EmptyState title="Pas de données" message="Aucune notification." />
              )}
            </div>
            <div className="dsi-card p-5">
              <h3 className="text-sm font-semibold mb-3">Types réels</h3>
              {typeBarsReal.length ? <HorizontalBars rows={typeBarsReal} /> : <EmptyState title="Pas de données" message="—" />}
            </div>
          </div>
          <div className="dsi-card p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold">Catégories & origines (démo)</h3>
              <DemoDataBanner workerName="Worker Notification" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <HorizontalBars rows={categoryBarsDemo} />
              <HorizontalBars rows={originBarsDemo} />
            </div>
          </div>
          <div className="dsi-card p-4 text-xs text-slate-500">
            Taux de réussite / canaux : données non disponibles — Worker Notification non branché.
          </div>
        </div>
      )}

      {/* CONTROL */}
      {tab === 'control' && (
        <div className="space-y-4">
          <DemoDataBanner workerName="Worker Notification" />
          <div className="dsi-card overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">Catégorie / Canal</th>
                  <th className="px-4 py-3">État</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {DEMO_CONTROL.map((row) => (
                  <tr key={row.cat}>
                    <td className="px-4 py-3 font-medium text-slate-900">{row.cat}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                        row.state === 'Activé' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                        row.state === 'Préparation' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-slate-100 text-slate-500 border-slate-200'
                      }`}>{row.state}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500">Aucune action de bascule n'appelle de contrat inexistant. Affichage préparatoire uniquement.</p>
        </div>
      )}

      {/* Detail fiche */}
      {detailNotif && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setDetailNotif(null)} />
          <div className="relative w-full max-w-lg dsi-card p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">{detailNotif.titre}</h3>
                <p className="text-xs text-slate-500">{detailNotif.type} · {detailNotif.statut}</p>
              </div>
              <button onClick={() => setDetailNotif(null)} className="p-2 rounded-xl hover:bg-slate-100"><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-700">{detailNotif.message}</p>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {[
                ['Créée le', detailNotif.created_at ? new Date(detailNotif.created_at).toLocaleString('fr-FR') : '—'],
                ['Auteur', detailNotif.cree_par_id ? `Admin #${detailNotif.cree_par_id}` : '—'],
                ['Début affichage', detailNotif.date_debut_affichage ? new Date(detailNotif.date_debut_affichage).toLocaleString('fr-FR') : 'Immédiat'],
                ['Fin affichage', detailNotif.date_fin_affichage ? new Date(detailNotif.date_fin_affichage).toLocaleString('fr-FR') : 'Indéterminée'],
                ['Canal', 'In-App (actuel)'],
                ['Origine', 'Worker Admin'],
              ].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-[10px] uppercase text-slate-400 font-semibold">{k}</p>
                  <p className="mt-1 font-medium text-slate-800">{v}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-400">Champs destinataires / multi-canal : emplacements pour Worker Notification.</p>
          </div>
        </div>
      )}

      <NotificationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateOrUpdate}
        initialData={editingNotif}
      />
    </div>
  );
}
